import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Briefcase,
  MapPin,
  Clock,
  DollarSign,
  ChevronRight,
  Send,
  CheckCircle,
  X,
  Building,
  Calendar,
  FileText,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Careers() {
  const [postings, setPostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPosting, setSelectedPosting] = useState(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [application, setApplication] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    job_posting_id: '',
    desired_coverage_areas: '',
    employment_type_preference: 'Full-time',
    available_start_date: '',
    cover_letter: '',
  });

  useEffect(() => {
    fetchPostings();
    fetchEmploymentTypes();
  }, []);

  const fetchPostings = async () => {
    try {
      const response = await axios.get(`${API}/ats/job-postings/public`);
      setPostings(response.data);
    } catch (error) {
      console.error('Failed to load job postings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmploymentTypes = async () => {
    try {
      const response = await axios.get(`${API}/ats/employment-types`);
      setEmploymentTypes(response.data.employment_types);
    } catch (error) {
      console.error('Failed to load employment types:', error);
    }
  };

  const handleApply = (posting) => {
    setSelectedPosting(posting);
    setApplication({
      ...application,
      job_posting_id: posting.id,
      employment_type_preference: posting.employment_type || 'Full-time',
    });
    setShowApplicationForm(true);
    setSubmitted(false);
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    
    if (!application.first_name || !application.last_name || !application.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(application.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...application,
        desired_coverage_areas: application.desired_coverage_areas
          ? application.desired_coverage_areas.split(',').map((a) => a.trim())
          : [],
      };
      
      await axios.post(`${API}/ats/public/apply`, payload);
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit application';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowApplicationForm(false);
    setSelectedPosting(null);
    setSubmitted(false);
    setApplication({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      job_posting_id: '',
      desired_coverage_areas: '',
      employment_type_preference: 'Full-time',
      available_start_date: '',
      cover_letter: '',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section */}
      <div className="bg-primary text-white py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="mb-6">
            <div className="text-3xl font-bold tracking-tight">
              Elite Home Rehab
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Join Our Team</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Make a difference in patients' lives. Join Elite Home Rehab and be part of a team
            dedicated to providing exceptional home health care services.
          </p>
        </div>
      </div>

      {/* Job Listings */}
      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Open Positions</h2>
          <p className="text-gray-600">
            {postings.length} {postings.length === 1 ? 'position' : 'positions'} available
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">
            Loading job openings...
          </div>
        ) : postings.length === 0 ? (
          <Card className="p-12 text-center">
            <Briefcase className="mx-auto mb-4 text-gray-400" size={48} strokeWidth={1.5} />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Open Positions</h3>
            <p className="text-gray-500">
              We don't have any open positions at the moment. Please check back later!
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {postings.map((posting) => (
              <Card
                key={posting.id}
                className="p-6 hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-primary"
                onClick={() => handleApply(posting)}
                data-testid={`job-posting-${posting.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {posting.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                      <span className="flex items-center gap-1">
                        <Briefcase size={16} />
                        {posting.position_type}
                      </span>
                      {posting.location && (
                        <span className="flex items-center gap-1">
                          <MapPin size={16} />
                          {posting.location}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock size={16} />
                        {posting.employment_type}
                      </span>
                      {posting.salary_range && (
                        <span className="flex items-center gap-1">
                          <DollarSign size={16} />
                          {posting.salary_range}
                        </span>
                      )}
                    </div>
                    {posting.coverage_areas && posting.coverage_areas.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {posting.coverage_areas.map((area, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {area}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button className="gap-2 whitespace-nowrap" data-testid={`apply-btn-${posting.id}`}>
                    Apply Now
                    <ChevronRight size={16} />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Company Info */}
        <div className="mt-16 grid md:grid-cols-3 gap-8">
          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building className="text-primary" size={24} />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">About Us</h3>
            <p className="text-sm text-gray-600">
              Elite Home Rehab is a leading provider of home health rehabilitation services
              across Texas and Louisiana.
            </p>
          </Card>
          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="text-primary" size={24} />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Benefits</h3>
            <p className="text-sm text-gray-600">
              Competitive pay, flexible scheduling, professional development opportunities,
              and a supportive team environment.
            </p>
          </Card>
          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="text-primary" size={24} />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Requirements</h3>
            <p className="text-sm text-gray-600">
              Valid state license for your discipline, reliable transportation, and a passion
              for patient care.
            </p>
          </Card>
        </div>
      </div>

      {/* Application Modal */}
      {showApplicationForm && selectedPosting && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
          <Card className="w-full max-w-2xl p-6 m-4 max-h-[90vh] overflow-y-auto">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="text-green-600" size={32} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h2>
                <p className="text-gray-600 mb-6">
                  Thank you for applying for {selectedPosting.title}. We will review your
                  application and contact you soon.
                </p>
                <Button onClick={closeModal} data-testid="close-success-btn">
                  Close
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Apply for Position</h2>
                    <p className="text-sm text-gray-600">{selectedPosting.title}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={closeModal}>
                    <X size={18} />
                  </Button>
                </div>

                <form onSubmit={handleSubmitApplication} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        First Name *
                      </label>
                      <Input
                        value={application.first_name}
                        onChange={(e) =>
                          setApplication({ ...application, first_name: e.target.value })
                        }
                        placeholder="John"
                        required
                        data-testid="apply-first-name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Last Name *
                      </label>
                      <Input
                        value={application.last_name}
                        onChange={(e) =>
                          setApplication({ ...application, last_name: e.target.value })
                        }
                        placeholder="Smith"
                        required
                        data-testid="apply-last-name"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Email *
                      </label>
                      <Input
                        type="email"
                        value={application.email}
                        onChange={(e) =>
                          setApplication({ ...application, email: e.target.value })
                        }
                        placeholder="john.smith@email.com"
                        required
                        data-testid="apply-email"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Phone
                      </label>
                      <Input
                        value={application.phone}
                        onChange={(e) =>
                          setApplication({ ...application, phone: e.target.value })
                        }
                        placeholder="(555) 123-4567"
                        data-testid="apply-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Street Address
                    </label>
                    <Input
                      value={application.address}
                      onChange={(e) =>
                        setApplication({ ...application, address: e.target.value })
                      }
                      placeholder="123 Main St"
                      data-testid="apply-address"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        City
                      </label>
                      <Input
                        value={application.city}
                        onChange={(e) =>
                          setApplication({ ...application, city: e.target.value })
                        }
                        placeholder="Houston"
                        data-testid="apply-city"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        State
                      </label>
                      <Input
                        value={application.state}
                        onChange={(e) =>
                          setApplication({ ...application, state: e.target.value })
                        }
                        placeholder="TX"
                        data-testid="apply-state"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Zip Code
                      </label>
                      <Input
                        value={application.zip_code}
                        onChange={(e) =>
                          setApplication({ ...application, zip_code: e.target.value })
                        }
                        placeholder="77001"
                        data-testid="apply-zip"
                      />
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h3 className="font-medium text-gray-900 mb-3">Position Details</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700">
                          Employment Preference
                        </label>
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={application.employment_type_preference}
                          onChange={(e) =>
                            setApplication({
                              ...application,
                              employment_type_preference: e.target.value,
                            })
                          }
                          data-testid="apply-employment-type"
                        >
                          {employmentTypes.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">
                          Available Start Date
                        </label>
                        <Input
                          type="date"
                          value={application.available_start_date}
                          onChange={(e) =>
                            setApplication({
                              ...application,
                              available_start_date: e.target.value,
                            })
                          }
                          data-testid="apply-start-date"
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="text-sm font-medium text-gray-700">
                        Preferred Coverage Areas (comma-separated)
                      </label>
                      <Input
                        value={application.desired_coverage_areas}
                        onChange={(e) =>
                          setApplication({
                            ...application,
                            desired_coverage_areas: e.target.value,
                          })
                        }
                        placeholder="e.g., Houston, Katy, Sugar Land"
                        data-testid="apply-coverage-areas"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Cover Letter / Additional Information
                    </label>
                    <textarea
                      className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={application.cover_letter}
                      onChange={(e) =>
                        setApplication({ ...application, cover_letter: e.target.value })
                      }
                      placeholder="Tell us about yourself, your experience, and why you'd be a great fit for this position..."
                      data-testid="apply-cover-letter"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button type="button" variant="outline" onClick={closeModal}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={submitting} data-testid="submit-application-btn">
                      <Send size={16} className="mr-2" />
                      {submitting ? 'Submitting...' : 'Submit Application'}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
